import { useState } from 'react';
import { ShoppingCart, Trash2, Edit, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import CreditCardFields from './CreditCardFields';

export default function RegistrationCart({
  cart,
  events,
  currentPrice,
  paymentMethod,
  setPaymentMethod,
  cardForm,
  setCardForm,
  billingData,
  setBillingData,
  billingParticipantIndex,
  setBillingParticipantIndex,
  onRemove,
  onEdit,
  onCheckout,
  onClear,
  submitting,
  formatCPF,
  formatCNPJ,
}) {
  const getItemTotal = (item) => {
    const optionalItemsTotal = item.optional_items_total || 0;
    return currentPrice + optionalItemsTotal;
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + getItemTotal(item), 0);
  };

  if (cart.length === 0) {
    return (
      <div data-testid="empty-cart" className="bg-card border border-border rounded-lg p-8 text-center">
        <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-foreground/30" />
        <h3 className="text-xl font-bold text-foreground mb-2">Carrinho Vazio</h3>
        <p className="text-foreground/70">Adicione participantes usando o formulário acima.</p>
      </div>
    );
  }

  return (
    <div data-testid="registration-cart" className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="bg-primary/10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-primary" />
          <span className="font-bold text-primary">{cart.length} participante(s)</span>
        </div>
        <Button variant="ghost" size="sm" onClick={onClear} className="text-destructive hover:text-destructive/90">
          Limpar Carrinho
        </Button>
      </div>

      <div className="divide-y divide-border">
        {cart.map((item, index) => (
          <div key={item.cartItemId} data-testid={`cart-item-${index}`} className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-bold text-foreground">{item.nome} {item.sobrenome}</p>
                <p className="text-sm text-foreground/70">{item.email}</p>
                <p className="text-sm text-foreground/70">{item.percurso} - {item.categoria}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="font-bold text-accent">R$ {getItemTotal(item).toFixed(2)}</span>
                  {item.optional_items_total > 0 && (
                    <p className="text-xs text-foreground/50">
                      (Inscrição: R$ {currentPrice.toFixed(2)} + Adicionais: R$ {item.optional_items_total.toFixed(2)})
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => onEdit(index)} className="rounded-sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => onRemove(index)} className="rounded-sm">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
            {/* Show optional items if any */}
            {item.optional_items && item.optional_items.length > 0 && (
              <div className="mt-2 pl-4 border-l-2 border-green-300">
                <p className="text-xs font-bold text-green-700 mb-1">Itens Adicionais:</p>
                {item.optional_items.map((optItem, optIdx) => (
                  <p key={optIdx} className="text-xs text-green-600">
                    • {optItem.name} x{optItem.quantity} = R$ {(optItem.price * optItem.quantity).toFixed(2)}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-border p-4 bg-secondary/50">
        <div className="flex justify-between items-center text-xl font-bold mb-6">
          <span className="text-primary">Total:</span>
          <span className="text-accent">R$ {getCartTotal().toFixed(2)}</span>
        </div>

        {/* Billing Selection */}
        <div className="space-y-4 mb-6">
          <Label className="text-sm font-bold uppercase tracking-widest text-primary">
            Quem será o responsável pelo pagamento?
          </Label>
          <div className="flex flex-wrap gap-2">
            {cart.map((item, index) => (
              <Button
                key={item.cartItemId}
                type="button"
                variant={billingParticipantIndex === index ? 'default' : 'outline'}
                onClick={() => setBillingParticipantIndex(index)}
                className="rounded-sm text-xs"
              >
                <Users className="w-4 h-4 mr-1" />
                {item.nome} {item.sobrenome}
              </Button>
            ))}
          </div>
        </div>

        {/* Different billing data option */}
        <div className="space-y-4 mb-6 p-4 border border-dashed border-border rounded-lg">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="usar_dados_faturamento"
              checked={billingData.usar_dados_faturamento}
              onChange={(e) => setBillingData({ ...billingData, usar_dados_faturamento: e.target.checked })}
              className="w-4 h-4"
            />
            <Label htmlFor="usar_dados_faturamento" className="text-sm font-bold cursor-pointer">
              Usar dados diferentes para faturamento
            </Label>
          </div>

          {billingData.usar_dados_faturamento && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="faturamento_tipo"
                    value="pf"
                    checked={billingData.faturamento_tipo === 'pf'}
                    onChange={(e) => setBillingData({ ...billingData, faturamento_tipo: e.target.value })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-bold">Pessoa Física</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="faturamento_tipo"
                    value="pj"
                    checked={billingData.faturamento_tipo === 'pj'}
                    onChange={(e) => setBillingData({ ...billingData, faturamento_tipo: e.target.value })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-bold">Pessoa Jurídica</span>
                </label>
              </div>

              {billingData.faturamento_tipo === 'pj' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-primary mb-1">Razão Social *</Label>
                    <input
                      type="text"
                      value={billingData.faturamento_razao_social}
                      onChange={(e) => setBillingData({ ...billingData, faturamento_razao_social: e.target.value })}
                      className="w-full bg-white border border-border rounded-sm h-10 px-3 text-sm"
                      required={billingData.usar_dados_faturamento && billingData.faturamento_tipo === 'pj'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-primary mb-1">CNPJ *</Label>
                    <input
                      type="text"
                      value={billingData.faturamento_cnpj}
                      onChange={(e) => setBillingData({ ...billingData, faturamento_cnpj: formatCNPJ(e.target.value) })}
                      className="w-full bg-white border border-border rounded-sm h-10 px-3 text-sm"
                      maxLength={18}
                      required={billingData.usar_dados_faturamento && billingData.faturamento_tipo === 'pj'}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase text-primary mb-1">Nome Completo *</Label>
                    <input
                      type="text"
                      value={billingData.faturamento_nome}
                      onChange={(e) => setBillingData({ ...billingData, faturamento_nome: e.target.value })}
                      className="w-full bg-white border border-border rounded-sm h-10 px-3 text-sm"
                      required={billingData.usar_dados_faturamento && billingData.faturamento_tipo === 'pf'}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase text-primary mb-1">CPF *</Label>
                    <input
                      type="text"
                      value={billingData.faturamento_cpf}
                      onChange={(e) => setBillingData({ ...billingData, faturamento_cpf: formatCPF(e.target.value) })}
                      className="w-full bg-white border border-border rounded-sm h-10 px-3 text-sm"
                      maxLength={14}
                      required={billingData.usar_dados_faturamento && billingData.faturamento_tipo === 'pf'}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Payment Method */}
        <div className="space-y-4 mb-6">
          <Label className="text-sm font-bold uppercase tracking-widest text-primary">
            Forma de Pagamento
          </Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => setPaymentMethod('pix')}
              className={`p-4 border-2 rounded-lg text-center transition-all ${
                paymentMethod === 'pix'
                  ? 'border-accent bg-accent/10'
                  : 'border-border hover:border-accent/50'
              }`}
            >
              <span className="text-2xl block mb-2">📱</span>
              <span className="font-bold text-sm">PIX</span>
              <span className="text-xs text-foreground/70 block">Aprovação imediata</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod('boleto')}
              className={`p-4 border-2 rounded-lg text-center transition-all ${
                paymentMethod === 'boleto'
                  ? 'border-accent bg-accent/10'
                  : 'border-border hover:border-accent/50'
              }`}
            >
              <span className="text-2xl block mb-2">📄</span>
              <span className="font-bold text-sm">Boleto</span>
              <span className="text-xs text-foreground/70 block">Até 3 dias úteis</span>
            </button>
            <button
              type="button"
              onClick={() => setPaymentMethod('credit_card')}
              className={`p-4 border-2 rounded-lg text-center transition-all ${
                paymentMethod === 'credit_card'
                  ? 'border-accent bg-accent/10'
                  : 'border-border hover:border-accent/50'
              }`}
            >
              <span className="text-2xl block mb-2">💳</span>
              <span className="font-bold text-sm">Cartão</span>
              <span className="text-xs text-foreground/70 block">Crédito</span>
            </button>
          </div>
        </div>

        {paymentMethod === 'credit_card' && (
          <div className="mb-6">
            <CreditCardFields
              cardForm={cardForm}
              setCardForm={setCardForm}
              totalAmount={getCartTotal()}
            />
          </div>
        )}

        <Button
          data-testid="checkout-button"
          onClick={onCheckout}
          disabled={submitting}
          className="w-full bg-accent text-accent-foreground hover:bg-accent/90 uppercase tracking-widest font-bold px-8 py-6 rounded-sm text-lg"
        >
          {submitting ? 'Processando...' : 'Finalizar Inscrição'}
        </Button>
      </div>
    </div>
  );
}
